/**
 * Support Tasks Types
 * Types for the support task management feature
 */

import type { UUID } from "./common.types";

/**
 * Task types available in the system
 */
export type SupportTaskType =
  | "birthday_message"
  | "28_day_past_due_legacy_retention"
  | "closed_card_hard_decline"
  | "closed_card_soft_decline"
  | "general"
  | "manual_fortegra_account_cancellation"
  | "manual_lockton_account_cancellation"
  | "manual_nonfortegra_account_cancellation"
  | "outbound_discount_50_states_winbacks"
  | "outbound_fortegra_legal_heat_leads"
  | "outbound_fortegra_loc"
  | "outbound_fortegra_winbacks"
  | "outbound_gsw_offer_fortegra_legal_heat_leads"
  | "outbound_gsw_offer_non_fortegra_legal_heat_leads"
  | "outbound_landing_page_leads"
  | "outbound_non_fortegra_legal_heat_leads"
  | "outbound_non_fortegra_loc"
  | "outbound_non_fortegra_winbacks"
  | "outbound_winback_voucher"
  | "outbound_winbacks"
  // Recovery outreach cadence (added 2026-09-09). These carry the exact PascalCase spelling the
  // requesting system sends, unlike the snake_case values above -- the API stores task_type
  // verbatim, so the value here has to match the wire format character for character or a
  // manually-created task and an API-created one would be two different types in every report.
  | "RecoveryOutreachDay3"
  | "RecoveryOutreachDay7"
  | "RecoveryOutreachDay15"
  // Expired outreach cadence (added 2026-09-16, requested by the business). Same PascalCase wire
  // format and the same Day 3/7/15 shape as the recovery cadence above, and for the same reason:
  // the API stores task_type verbatim, so this must match character for character or a
  // manually-created task and an API-created one become two different types in every report.
  | "ExpiredOutreachDay3"
  | "ExpiredOutreachDay7"
  | "ExpiredOutreachDay15"
  // Hard-decline outreach cadence (added 2026-10-15, requested by the business). Same PascalCase
  // wire format as the two cadences above (note the Day 1/2/7 shape), and for the same reason: the
  // API stores task_type verbatim, so this must match character for character or a
  // manually-created task and an API-created one become two different types in every report.
  | "HardDeclineOutreachDay1"
  | "HardDeclineOutreachDay2"
  | "HardDeclineOutreachDay7";

/**
 * Display names for task types
 */
export const TASK_TYPE_LABELS: Record<SupportTaskType, string> = {
  birthday_message: "Birthday Message",
  "28_day_past_due_legacy_retention":
    "28 Day Past Due Legacy Retention Message",
  closed_card_hard_decline: "Hard Decline",
  closed_card_soft_decline: "Soft Decline",
  general: "General Task",
  manual_fortegra_account_cancellation:
    "Manual - Fortegra Account Cancellation",
  manual_lockton_account_cancellation: "Manual Lockton Account Cancellation",
  manual_nonfortegra_account_cancellation:
    "Manual - Non-Fortegra Account Cancellation",
  outbound_discount_50_states_winbacks:
    "Outbound - Discount 50 States Winbacks",
  outbound_fortegra_legal_heat_leads: "Outbound - Fortegra Legal Heat Leads",
  outbound_fortegra_loc: "Outbound - Fortegra LOC",
  outbound_fortegra_winbacks: "Outbound - Fortegra Winbacks",
  outbound_gsw_offer_fortegra_legal_heat_leads:
    "Outbound - GSW Offer Fortegra Legal Heat Leads",
  outbound_gsw_offer_non_fortegra_legal_heat_leads:
    "Outbound - GSW Offer Non Fortegra Legal Heat Leads",
  outbound_landing_page_leads: "Outbound - Landing Page Leads",
  outbound_non_fortegra_legal_heat_leads:
    "Outbound - Non Fortegra Legal Heat Leads",
  outbound_non_fortegra_loc: "Outbound - Non Fortegra LOC",
  outbound_non_fortegra_winbacks: "Outbound - Non Fortegra Winbacks",
  outbound_winback_voucher: "Outbound Winback- Voucher",
  outbound_winbacks: "Outbound - Winbacks",
  RecoveryOutreachDay3: "Recovery Outreach - Day 3",
  RecoveryOutreachDay7: "Recovery Outreach - Day 7",
  RecoveryOutreachDay15: "Recovery Outreach - Day 15",
  ExpiredOutreachDay3: "Expired Payment - Day 3",
  ExpiredOutreachDay7: "Expired Payment - Day 7",
  ExpiredOutreachDay15: "Expired Payment - Day 15",
  HardDeclineOutreachDay1: "Hard Decline Outreach - Day 1",
  HardDeclineOutreachDay2: "Hard Decline Outreach - Day 2",
  HardDeclineOutreachDay7: "Hard Decline Outreach - Day 7",
};

/**
 * Grouping for the task-type dropdowns (requested by the support team, 2026-09-18).
 *
 * PURELY PRESENTATIONAL. `task_type` remains the stored and API-sent value, unchanged — the wire
 * spelling of every key below is untouched, which matters because the API stores task_type
 * verbatim and a drift here would make an API-created task and a manually-created one two
 * different types in every report.
 *
 * ⚠️ This map is DELIBERATELY NOT the source of which options exist. TASK_TYPE_LABELS is. A type
 * that is missing from every group below still appears, under "Other" — see groupedTaskTypeOptions.
 * Building the dropdown from the groups instead would mean that adding a task type and forgetting
 * to group it silently removes it from every dropdown, which is exactly the failure that made
 * these types unselectable before they were added to TASK_TYPE_LABELS in the first place.
 */
export const TASK_TYPE_GROUPS: ReadonlyArray<{
  label: string;
  types: ReadonlyArray<SupportTaskType>;
}> = [
  {
    // Both cadences live here: the business treats "Recovery Outreach" as the umbrella for the
    // whole payment-recovery workflow, not just the RecoveryOutreachDay* wire type.
    label: "Recovery Outreach",
    types: [
      "ExpiredOutreachDay3",
      "ExpiredOutreachDay7",
      "ExpiredOutreachDay15",
      "RecoveryOutreachDay3",
      "RecoveryOutreachDay7",
      "RecoveryOutreachDay15",
      "closed_card_hard_decline",
      // Placed next to the legacy hard-decline type they follow up on (2026-10-15).
      "HardDeclineOutreachDay1",
      "HardDeclineOutreachDay2",
      "HardDeclineOutreachDay7",
      "closed_card_soft_decline",
      // TODO(2026-09-18): "Declined Payment", "Auto Pay Off" and "Lapse of Coverage" belong here
      // too. They are NOT listed because they do not exist as task types yet and the exact wire
      // strings the API sends are not known. Guessing them would produce options that look right
      // and never match an API-created task.
    ],
  },
];

/** A dropdown option list grouped for display. */
export interface TaskTypeOptionGroup {
  label: string;
  options: Array<{ value: SupportTaskType; label: string }>;
}

/** The group shown for any task type not placed in TASK_TYPE_GROUPS. */
export const UNGROUPED_TASK_TYPE_LABEL = "Other";

/**
 * Every task type in TASK_TYPE_LABELS, grouped for display — each one exactly once.
 *
 * Anything not named in TASK_TYPE_GROUPS falls into the "Other" group rather than disappearing,
 * so the dropdown can never show fewer options than TASK_TYPE_LABELS defines. A group entry naming
 * a type that no longer exists is skipped rather than rendering a dead option.
 */
export function groupedTaskTypeOptions(): TaskTypeOptionGroup[] {
  const all = Object.keys(TASK_TYPE_LABELS) as SupportTaskType[];
  const claimed = new Set<SupportTaskType>();
  const groups: TaskTypeOptionGroup[] = [];

  for (const group of TASK_TYPE_GROUPS) {
    const options = group.types
      .filter((t) => t in TASK_TYPE_LABELS && !claimed.has(t))
      .map((t) => {
        claimed.add(t);
        return { value: t, label: TASK_TYPE_LABELS[t] };
      });
    if (options.length > 0) {
      groups.push({ label: group.label, options });
    }
  }

  const rest = all
    .filter((t) => !claimed.has(t))
    .map((t) => ({ value: t, label: TASK_TYPE_LABELS[t] }));
  if (rest.length > 0) {
    groups.push({ label: UNGROUPED_TASK_TYPE_LABEL, options: rest });
  }
  return groups;
}

/**
 * Task-type FAMILIES for the FILTER dropdowns only (reported 2026-10-16).
 *
 * Support picked "Hard Decline" in the task-type filter and saw "No pending tasks found" while
 * 12 "Hard Decline Outreach - Day 1/2/7" tasks were pending: the "Hard Decline" option was the
 * single legacy wire type `closed_card_hard_decline` (0 rows ever on PROD), and the Day 1/2/7
 * cadence added 2026-10-15 is three OTHER wire types. A family option filters on ALL of its
 * member types at once, and every surface that applies the filter (lists, tab counts, header
 * counts, select-all, analytics) resolves it through resolveTaskTypeFilter, so they agree.
 *
 * ⚠️ A family is a FILTER value, never a task_type. It must never reach a write (create task,
 * change type) — those dropdowns keep using groupedTaskTypeOptions(), which has no families.
 * The "family:" prefix cannot collide with a real wire type (none contains a colon).
 */
export const TASK_TYPE_FILTER_FAMILIES = [
  {
    value: "family:hard_decline",
    label: "Hard Decline - All",
    types: [
      "closed_card_hard_decline",
      "HardDeclineOutreachDay1",
      "HardDeclineOutreachDay2",
      "HardDeclineOutreachDay7",
    ],
    // Inside the filter dropdown the legacy member is relabelled so two options are never both
    // called "Hard Decline". Its stored label (TASK_TYPE_LABELS) is unchanged everywhere else.
    memberLabels: {
      closed_card_hard_decline: "Hard Decline - Closed Card (original type)",
    },
  },
] as const satisfies ReadonlyArray<{
  value: `family:${string}`;
  label: string;
  types: ReadonlyArray<SupportTaskType>;
  memberLabels: Partial<Record<SupportTaskType, string>>;
}>;

export type TaskTypeFamilyFilter =
  (typeof TASK_TYPE_FILTER_FAMILIES)[number]["value"];

/** What a task-type FILTER may hold: one wire type, a family, or everything. */
export type TaskTypeFilterValue =
  | SupportTaskType
  | TaskTypeFamilyFilter
  | "all";

/**
 * The task_type values a filter value matches, or null for "no task-type filter".
 * A family expands to its member types; any other non-empty value is a single wire type
 * (passed through unchanged, exactly as the filter behaved before families existed).
 */
export function resolveTaskTypeFilter(
  value: string | null | undefined,
): string[] | null {
  if (!value || value === "all") {
    return null;
  }
  const family = TASK_TYPE_FILTER_FAMILIES.find((f) => f.value === value);
  return family ? [...family.types] : [value];
}

/** A filter dropdown option group: real types plus any family options. */
export interface TaskTypeFilterOptionGroup {
  label: string;
  options: Array<{ value: TaskTypeFilterValue; label: string }>;
}

/**
 * groupedTaskTypeOptions() plus the family options, for FILTER dropdowns only.
 * Each family option is placed immediately before its first member that appears in a group (or
 * at the top of "Other" if none does), and its members keep their own options so a single
 * Day can still be picked on its own.
 */
export function taskTypeFilterOptions(): TaskTypeFilterOptionGroup[] {
  const groups: TaskTypeFilterOptionGroup[] = groupedTaskTypeOptions().map(
    (g) => ({ label: g.label, options: [...g.options] }),
  );
  for (const family of TASK_TYPE_FILTER_FAMILIES) {
    const familyOption = { value: family.value, label: family.label };
    const members = new Set<string>(family.types);
    const labels: Partial<Record<string, string>> = family.memberLabels;
    let placed = false;
    for (const group of groups) {
      group.options = group.options.map((o) =>
        labels[o.value] ? { value: o.value, label: labels[o.value]! } : o,
      );
      if (placed) {
        continue;
      }
      const at = group.options.findIndex((o) => members.has(o.value));
      if (at >= 0) {
        group.options.splice(at, 0, familyOption);
        placed = true;
      }
    }
    if (!placed) {
      groups.push({
        label: UNGROUPED_TASK_TYPE_LABEL,
        options: [familyOption],
      });
    }
  }
  return groups;
}

/**
 * Task status values
 */
export type TaskStatus = "pending" | "in_progress" | "completed" | "cancelled";

/**
 * Tab keys for the support tasks page.
 * Includes virtual tabs:
 *   - "past_due": pending/in_progress tasks past their due_date
 *   - "unassigned": pending/in_progress tasks with no agent (leadership
 *     triage view; 2026-05-20 request — replaces the prior Cancelled
 *     tab in the overview).
 * "cancelled" is still a valid TaskStatus on the DB side, just not a
 * surfaced tab on the overview.
 */
export type TaskTabKey =
  | "pending"
  | "in_progress"
  | "completed"
  | "past_due"
  | "unassigned";

/**
 * Display names and colors for task statuses
 */
export const TASK_STATUS_CONFIG: Record<
  TaskStatus,
  { label: string; color: string; bgColor: string }
> = {
  pending: {
    label: "Pending",
    color: "text-amber-800",
    bgColor: "bg-amber-100",
  },
  in_progress: {
    label: "In Progress",
    color: "text-blue-800",
    bgColor: "bg-blue-100",
  },
  completed: {
    label: "Completed",
    color: "text-green-800",
    bgColor: "bg-green-100",
  },
  cancelled: {
    label: "Cancelled",
    color: "text-gray-800",
    bgColor: "bg-gray-100",
  },
};

/**
 * Customer snapshot stored with each task
 * Denormalized data at time of task creation for historical accuracy
 */
export interface CustomerSnapshot {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  member_id?: string;
  plan_type?: string;
  account_status?: string;
  days_past_due?: number;
  account_balance?: string;
  invoice_balance?: string;
  invoice_amount?: string;
  invoice_number?: string;
  address?: string;
  region?: string;
  tags?: string[];
  // Allow additional fields from Zuora imports
  [key: string]: string | number | boolean | string[] | undefined;
}

/**
 * Task priority levels (2026-05-23). Four-level taxonomy. Default 'medium'.
 * Fortegra Cancellation tasks auto-set to 'high' server-side via a
 * BEFORE INSERT trigger; see migration 2026-05-23-002.
 */
export type TaskPriority = "low" | "medium" | "high" | "urgent";

export const TASK_PRIORITY_CONFIG: Record<
  TaskPriority,
  { label: string; color: string; bgColor: string; weight: number }
> = {
  urgent: {
    label: "Urgent",
    color: "text-red-800",
    bgColor: "bg-red-100",
    weight: 3,
  },
  high: {
    label: "High",
    color: "text-orange-800",
    bgColor: "bg-orange-100",
    weight: 2,
  },
  medium: {
    label: "Medium",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
    weight: 1,
  },
  low: {
    label: "Low",
    color: "text-blue-700",
    bgColor: "bg-blue-50",
    weight: 0,
  },
};

/**
 * Database support task record
 */
export interface SupportTask {
  id: UUID;
  task_number: number;
  /** Stable identifier supplied by the upstream support-task source. */
  external_task_id?: string | null;
  // Nullable since 2026-05-15: support_tasks.agent_id is now nullable. NULL
  // means the task is unassigned (admin triage queue). External integrations
  // POSTing with agent_name = "-" land in this state.
  agent_id: UUID | null;
  contact_id: UUID;
  task_type: SupportTaskType;
  status: TaskStatus;
  // 2026-05-23: four-level priority. Default 'medium'; Fortegra Cancellation
  // auto-set to 'high'. Operator-overridable.
  priority: TaskPriority;
  due_date: string | null;
  customer_snapshot: CustomerSnapshot;
  notes?: string;
  tags?: string[];
  interaction_id?: UUID;
  created_at: string;
  opened_at?: string;
  completed_at?: string;
  import_batch_id?: UUID;
  source_invoice_number?: string;
  created_by?: UUID;
  updated_at: string;
  // Escalation fields
  escalated_to?: UUID;
  escalated_at?: string;
  escalation_reason?: string;
  original_agent_id?: UUID;
  // Joined fields — note: support_agents has a `name` column, not
  // `full_name`. The previous typing was drifted from the actual query
  // (queries select `name`) and accessing `.full_name` would silently
  // return undefined. Fixed 2026-05-15.
  agent?: {
    id: UUID;
    name: string;
    email: string;
  };
  contact?: {
    id: UUID;
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string;
  };
  // Joined escalation target agent
  escalated_to_agent?: {
    id: UUID;
    name: string;
    email: string;
  };
  // Follow-up chaining
  parent_task_id?: UUID;
  // Joined original agent (before escalation)
  original_agent?: {
    id: UUID;
    name: string;
    email: string;
  };
  // Linked interactions (interactions that have support_task_id pointing to this task)
  linked_interactions?: Array<{
    id: UUID;
    type: string;
    method?: string;
    interaction_date: string;
    outcome?: string;
    description?: string;
  }>;
}

/**
 * Data collected when completing or cancelling a task
 */
export interface TaskResolutionData {
  notes?: string;
  tags?: string[];
}

/**
 * Agent summary for the agent list view
 */
export interface SupportAgent {
  id: UUID;
  name: string;
  email: string;
  pending_count: number;
  in_progress_count: number;
  completed_count: number;
  cancelled_count: number;
  past_due_count: number;
  supervisor_id?: UUID;
  supervisor?: {
    id: UUID;
    name: string;
    email: string;
  };
  /** Set by getAgentByUserId; gates the "Escalated to me" view. */
  is_active?: boolean;
}

/**
 * Filter options for task list
 */
export interface TaskFilter {
  /** A wire type, a family (see TASK_TYPE_FILTER_FAMILIES) or "all". Resolve with resolveTaskTypeFilter. */
  taskType?: TaskTypeFilterValue;
  searchQuery?: string;
}

/**
 * Sort options for task list
 */
export type TaskSortField = "due_date" | "customer_name" | "task_type";
export type TaskSortOrder = "asc" | "desc";

/**
 * CSV Import batch tracking
 */
export interface SupportTaskImportBatch {
  id: UUID;
  file_name: string;
  file_size_bytes?: number;
  total_rows: number;
  contacts_created: number;
  contacts_updated: number;
  tasks_created: number;
  errors: number;
  error_details: ImportError[];
  status: "processing" | "completed" | "failed";
  created_at: string;
  completed_at?: string;
  created_by?: UUID;
}

/**
 * Import error detail
 */
export interface ImportError {
  row: number;
  field?: string;
  message: string;
}

/**
 * CSV row data for import (matches PostCardData format)
 */
export interface CSVTaskRow {
  "Invoice Date": string;
  "Invoice Days": string;
  State: string;
  "Product Name": string;
  "Account Status": string;
  Title?: string;
  "Account Number": string;
  "Account Name": string;
  "Account Balance": string;
  "Invoice Balance": string;
  "Invoice Amount": string;
  InvoiceNumber: string;
  "Mobile Phone"?: string;
  Email?: string;
  Address1?: string;
  Address2?: string;
  City?: string;
  "Postal Code"?: string;
  Region?: string;
  "Due Date": string;
  "Zuora Account GUID": string;
  "Agent Name": string;
  "Agent Email"?: string;
  // Optional: Direct agent ID lookup (external_agent_id in support_agents table)
  AgentId?: string;
}

/**
 * Import result summary
 */
export interface ImportResult {
  batch_id: UUID;
  total_rows: number;
  contacts_created: number;
  contacts_updated: number;
  tasks_created: number;
  tasks_updated: number;
  agents_created: number;
  errors: ImportError[];
}

/**
 * Legacy types for backward compatibility with JSON-based demo
 */
export interface LegacyTaskCustomer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  tags?: string[];
  [key: string]: string | number | boolean | string[] | undefined;
}

export interface LegacySupportTask {
  id: string;
  agent_id: string;
  agent_name: string;
  task_type: SupportTaskType;
  customer: LegacyTaskCustomer;
  due_date: string | null;
  status: TaskStatus;
  completed_at?: string;
  created_at: string;
  opened_at?: string;
  notes?: string;
  tags?: string[];
  completed?: boolean;
}

export interface LegacySupportTasksData {
  agents: Array<{
    id: string;
    name: string;
  }>;
  tasks: LegacySupportTask[];
}

/**
 * Resolution template for quick task completion
 */
export interface ResolutionTemplate {
  id: UUID;
  name: string;
  content: string;
  task_type: SupportTaskType | null;
  suggested_tags: string[];
  display_order: number;
  is_active: boolean;
}
